import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Button,
  LinearProgress,
  Chip,
  Link
} from '@mui/material';
import {
  InfoOutlined,
  ArrowUpward,
  Warning
} from '@mui/icons-material';
import axios from 'axios';

function ApiKeyLimits({ onUpgradeClick, refreshTrigger }) {
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {

    const fetchLimitsData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/usage/api-key-limits');
        setLimits(response.data.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching API key limits:', err);
        setError('Failed to load tier limit information');
      } finally {
        setLoading(false);
      }
    };
    fetchLimitsData();
  }, [refreshTrigger]);


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (!limits) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2">No limit information available</Typography>
      </Box>
    );
  }

  const { currentTier, apiKeys, upgrade } = limits;

  // Helper function to determine color based on percentage
  const getColorByPercentage = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'primary';
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {currentTier.name.charAt(0).toUpperCase() + currentTier.name.slice(1)} Tier Limits
      </Typography>

      {/* API Key usage bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            API Keys Used
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {apiKeys.current} / {currentTier.apiKeyLimit} ({apiKeys.percentUsed.toFixed(1)}%)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, apiKeys.percentUsed)}
          color={getColorByPercentage(apiKeys.percentUsed)}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {/* Available slots */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <InfoOutlined fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
        <Typography variant="body2" color="text.secondary">
          You have {apiKeys.available} API key {apiKeys.available === 1 ? 'slot' : 'slots'} available
        </Typography>
      </Box>

      {/* Warning for high usage */}
      {apiKeys.percentUsed >= 80 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Warning fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="body2" color="warning.main">
            You're approaching your API key limit. Consider upgrading your tier.
          </Typography>
        </Box>
      )}

      {/* Additional API key pricing
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Additional API keys cost €{currentTier.additionalApiKeyPrice.toFixed(2)} each (contact support to purchase).
      </Typography>
       */}
      {/* Upgrade section */}
      {upgrade && (
        <>
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Want More API Keys?
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Upgrade to {upgrade.name.charAt(0).toUpperCase() + upgrade.name.slice(1)} tier for {upgrade.additionalSlots} more API key {upgrade.additionalSlots === 1 ? 'slot' : 'slots'}.
            </Typography>

            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ mr: 2 }}>
                <Chip
                  label={`€${upgrade.priceIncrease.toFixed(2)}/month`}
                  color="info"
                  size="small"
                />
              </Box>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<ArrowUpward />}
                onClick={onUpgradeClick}
              >
                Upgrade Now
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
}

export default ApiKeyLimits; 